<template lang="pug">
div
  b-form-group(label="Buscar Empresa" label-for="empresa-autocomplete")
    vue-typeahead-bootstrap(
      v-model="empresaQuery"
      :data="empresas"
      :serializer="serializer"
      placeholder="Buscar por razón social o RUT"
      @input="handleInput"
      @hit="onEmpresaSelected"
      id="empresa-autocomplete"
      :class="{ 'is-invalid': showValidation }"
    )
    
    .mt-2(v-if="empresaQuery && empresaQuery.length >= 3 && empresas.length === 0 && !searching")
      small.text-muted No se encontraron empresas.
      br
      small.text-muted
        | ¿No existe la empresa?
        b-button(
          size="sm"
          variant="link"
          @click="goToCreateEmpresa"
          class="p-0 text-primary ml-1"
        ) Crear nueva empresa

    div(v-if="searching")
      small.text-muted
        b-spinner(small)
        |  Buscando empresas...

    b-form-invalid-feedback(v-if="showValidation")
      | Debe seleccionar una empresa.

  // Mostrar empresa seleccionada
  b-card.mt-3(v-if="empresaSeleccionada" no-body border-variant="primary")
    b-card-body
      .d-flex.justify-content-between.align-items-start
        div
          h6.mb-2 {{ empresaSeleccionada.razon_social }}
          p.mb-1.text-muted
            small
              strong RUT:
              |  {{ empresaSeleccionada.rut }}
          p.mb-0.text-muted(v-if="empresaSeleccionada.email")
            small
              strong Email:
              |  {{ empresaSeleccionada.email }}
        b-button(
          size="sm"
          variant="outline-secondary"
          @click="clearEmpresa"
        )
          | Cambiar
</template>

<script>
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap'
import axios from '@/plugins/axios'

export default {
  name: 'EmpresaAutocomplete',
  components: {
    VueTypeaheadBootstrap,
  },
  props: {
    value: {
      type: [Object, Number],
      default: null,
    },
    showValidation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      empresaQuery: '',
      empresas: [],
      empresaSeleccionada: null,
      searching: false,
      searchTimeout: null,
    }
  },
  methods: {
    handleInput(value) {
      this.empresaQuery = value
      this.empresaSeleccionada = null
      this.$emit('input', null)
      this.$emit('empresa-cleared')

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.searchEmpresas(value)
      }, 500)
    },

    serializer(empresa) {
      return `${empresa.rut} - ${empresa.razon_social}`
    },

    async searchEmpresas(query) {
      if (!query || query.length < 3) {
        this.empresas = []
        return
      }

      this.searching = true
      try {
        const response = await this.searchEmpresasByQuery(query)
        this.empresas = response || []
      } catch (error) {
        console.error('Error buscando empresas:', error)
        this.empresas = []
        this.$bvToast.toast('Error al buscar empresas', {
          variant: 'danger',
          autoHideDelay: 3000,
        })
      } finally {
        this.searching = false
      }
    },

    onEmpresaSelected(empresa) {
      this.empresaSeleccionada = empresa
      this.empresaQuery = `${empresa.rut} - ${empresa.razon_social}`

      this.$emit('input', empresa.id)
      this.$emit('empresa-selected', empresa)
    },

    clearEmpresa() {
      this.empresaQuery = ''
      this.empresaSeleccionada = null
      this.empresas = []
      this.$emit('input', null)
      this.$emit('empresa-cleared')
    },

    goToCreateEmpresa() {
      // Emitir evento para que el padre maneje la navegación
      this.$emit('create-empresa-requested')

      // O navegar directamente si el router está disponible
      // this.$router.push({ name: 'crear-empresa' })
    },

    async searchEmpresasByQuery(query) {
      try {
        // Buscar empresas por RUT o razón social.
        // Si el texto tiene forma de RUT, sacamos el guión y el dígito verificador
        // (igual que RutAutocompleteOV) para no romper el match contra el RUT
        // guardado en la BD cuando se pega el RUT completo con guión.
        const rutPattern = /^[0-9.]+-?[0-9kK]?$/i
        const trimmedQuery = query.trim()
        const queryToSend = rutPattern.test(trimmedQuery)
          ? trimmedQuery.replace(/\./g, '').split('-')[0]
          : query
        const apiAddress = `api/clientes/?query=${encodeURIComponent(
          queryToSend
        )}&client_type=empresa`
        const user = JSON.parse(localStorage.getItem('user'))

        if (!user || !user.key) {
          console.warn('Usuario no autenticado')
          return []
        }

        const config = {
          headers: { Authorization: `Token ${user.key}` },
        }

        const response = await axios.get(apiAddress, config)

        // Filtrar solo empresas
        const empresas = response.data.filter(
          (cliente) => cliente.tipo_cliente === 'Empresa' && cliente.empresa
        )

        return empresas.map((cliente) => ({
          id: cliente.empresa.id,
          rut: cliente.empresa.rut,
          razon_social: cliente.empresa.razon_social,
          email: cliente.empresa.email,
          telefono: cliente.empresa.telefono,
          direccion: cliente.empresa.direccion,
          comuna: cliente.empresa.comuna,
          region: cliente.empresa.region,
        }))
      } catch (error) {
        console.error('Error en búsqueda de empresas:', error)
        throw error
      }
    },
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .vbt-autcomplete-list {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1050;
}

::v-deep .vbt-autcomplete-list .list-group-item {
  padding: 0.5rem 0.75rem;
  border: none;
  cursor: pointer;
}

::v-deep .vbt-autcomplete-list .list-group-item:hover,
::v-deep .vbt-autcomplete-list .list-group-item.active {
  background-color: #f8f9fa;
  color: #495057;
}

::v-deep .form-control.is-invalid {
  border-color: #dc3545;
}

.text-primary {
  color: #007bff !important;
}

.card {
  border-left: 3px solid #007bff;
}
</style>
